import React from 'react';
import {
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import { Control, Controller, FieldValues } from 'react-hook-form';
import { IconSelectFieldType, SelectOption } from '@/type';
import { useTranslation } from 'react-i18next';
import { buildFormFieldLabel } from '@/util';
import { StringSchema } from 'yup';

interface IconSelectControllerProps<TFieldValues extends FieldValues = FieldValues> {
  control: Control<TFieldValues>;
  selectField: IconSelectFieldType;
}

const IconSelectController = <TFieldValues extends FieldValues = FieldValues>({
  control,
  selectField,
}: IconSelectControllerProps<TFieldValues>) => {
  const { t } = useTranslation();

  const buildLabel = () =>
    buildFormFieldLabel(t(selectField.label), selectField.validation as StringSchema);

  return (
    <Controller
      name={selectField.name as any}
      control={control}
      render={({ field, fieldState }) => {
        const value = field.value;

        const selectedOption = selectField.options?.find(
          (option) => option.value === value,
        );

        return (
          <FormControl
            fullWidth
            error={!!fieldState.error}
            required={selectField.required}
          >
            <InputLabel shrink id={`select-label-${selectField.name}`}>
              {buildLabel()}
            </InputLabel>
            <Select
              labelId={`select-label-${selectField.name}`}
              label={buildLabel()}
              value={value ?? ''}
              onChange={(event: SelectChangeEvent<any>) => {
                field.onChange(event.target.value);
                selectField.onChange?.(event.target.value);
              }}
              onBlur={() => {
                field.onBlur();
                selectField?.onBlur?.(field.name);
              }}
              renderValue={(selected: any) => selectedOption?.label ?? ''}
              disabled={selectField.disabled}
              notched
            >
              {(selectField.options || []).map((option: SelectOption) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            {fieldState.error && (
              <FormHelperText>{fieldState.error.message}</FormHelperText>
            )}
          </FormControl>
        );
      }}
    />
  );
};

export default IconSelectController;
